import { lazy, type ComponentType } from 'react';
import { isChunkLoadError, attemptChunkReload, clearChunkReloadGuard } from './chunkReload';

/**
 * lazyWithRetry — wrapper kolem React.lazy() který řeší "stale chunk" problém.
 *
 * Po deployi mají JS chunky nové hashe. Browser se starým index.html chce načíst
 * starý chunk → 404 → Cloudflare Pages vrátí HTML → "'text/html' is not a valid
 * JavaScript MIME type" → error boundary.
 *
 * Strategie (od nejlevnějšího po nejtvrdší):
 *  1) Tiché retry importu (2×, s prodlevou) — přečká krátké okno propagace CDN.
 *  2) Jednorázový cache-busting reload (sdílený guard v chunkReload.ts) — obejde
 *     cachovaný index.html. Po úspěšném načtení se guard uvolní.
 *
 * Detekce + reload + guard jsou centralizované v ./chunkReload (jeden zdroj
 * pravdy sdílený s main.tsx i PageErrorBoundary) → žádný race mezi mechanismy.
 */

const IMPORT_RETRY_DELAYS_MS = [400, 1200];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      clearChunkReloadGuard(); // úspěch → uvolni guard pro příští deploy
      return mod;
    } catch (err: any) {
      if (!isChunkLoadError(err)) throw err;

      // 1) Tiché retry — přečká krátké okno propagace CDN.
      for (const delay of IMPORT_RETRY_DELAYS_MS) {
        await sleep(delay);
        try {
          const mod = await factory();
          clearChunkReloadGuard();
          return mod;
        } catch {
          // pořád padá → reload níže
        }
      }

      // 2) Chunk pořád nedostupný → jednorázový cache-busting reload.
      if (attemptChunkReload()) {
        console.warn('[lazyWithRetry] Stale chunk → cache-busting reload…', String(err?.message ?? err));
        return { default: (() => null) as unknown as T }; // browser se přenačítá
      }

      // Reload už jednou proběhl a chunk pořád padá → skutečná chyba.
      console.error('[lazyWithRetry] Chunk still failing after reload, propagating', String(err?.message ?? err));
      throw err;
    }
  });
}
